/**
 * AgentChatPanel — Shift Plan Explainer chat panel.
 *
 * NOTE: This component uses raw HTML/CSS for rendering (not Meridian components).
 * Pre-existing pattern from the original chat implementation.
 * Will be refactored to use Meridian components in a follow-up CR.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store';
import { selectStationCode, selectPlanId, selectSelectedDate } from '../../store/slices/shiftPlanSlice';
import { useAgentChat } from './useAgentChat';
import { AgentMode, ShiftPlanAgentPhase, MAX_QUESTION_LENGTH, ROUTES } from '../../utils/constants';
import { SPMSGetShiftPlanResponse } from '../../types/shiftPlan';
import { ShiftSearchType } from './PlanSelectionMessage';
import { getAllShiftPlans } from '../../services/shiftPlanService';
import AgentMessage from './AgentMessage';
import PlanSelectionMessage from './PlanSelectionMessage';
import './AgentChatPanel.scss';

/**
 * Only Review & Publish is allowed to auto-catch the open plan; every other page
 * starts the operator on the "choose a plan" flow so a stale or not-yet-generated
 * plan is never latched. The active route comes from the router via `useLocation`.
 */
const AgentChatPanel: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [inputWarning, setInputWarning] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Plan selection state
  const [phase, setPhaseState] = useState<ShiftPlanAgentPhase>(ShiftPlanAgentPhase.AWAITING_INPUT);
  const phaseRef = useRef<ShiftPlanAgentPhase>(ShiftPlanAgentPhase.AWAITING_INPUT);
  const [lockedPlanId, setLockedPlanId] = useState<string | null>(null);
  const [lockedDate, setLockedDate] = useState<string | null>(null);
  const [availablePlans, setAvailablePlans] = useState<SPMSGetShiftPlanResponse[]>([]);

  // Keep phase ref in sync for async callbacks
  const setPhase = (newPhase: ShiftPlanAgentPhase) => {
    phaseRef.current = newPhase;
    setPhaseState(newPhase);
  };

  // Redux state
  const stationCode = useAppSelector(selectStationCode) || '';
  const reduxPlanId = useAppSelector(selectPlanId);
  const reduxDate = useAppSelector(selectSelectedDate);

  // Track whether we're on Review & Publish. Read reactively from the router so
  // in-app navigation updates it (HashRouter navigates via pushState, which does
  // not fire native hashchange — useLocation is the reliable source).
  const location = useLocation();
  const isReviewPage = location.pathname === ROUTES.SHIFT_PLAN_REVIEW;

  const hasPlanContext = isReviewPage && !!(reduxPlanId && reduxDate);

  // Single chat instance — Shift Plan mode only
  const { messages, isLoading, sendMessage, clearMessages } = useAgentChat(
    stationCode,
    AgentMode.SHIFT_PLAN,
    lockedPlanId,
    lockedDate
  );

  // Initialize phase based on plan context
  useEffect(() => {
    if (phaseRef.current === ShiftPlanAgentPhase.AWAITING_INPUT || phaseRef.current === ShiftPlanAgentPhase.CONFIRMING) {
      if (hasPlanContext) {
        setLockedPlanId(reduxPlanId);
        setLockedDate(reduxDate);
        setPhase(ShiftPlanAgentPhase.CONFIRMING);
      } else {
        // No plan context: clear any previously locked plan so a stale plan
        // never flows into useAgentChat before the operator picks a new one.
        setLockedPlanId(null);
        setLockedDate(null);
        setPhase(ShiftPlanAgentPhase.AWAITING_INPUT);
      }
    }
  }, [hasPlanContext, reduxPlanId, reduxDate]);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current && messagesEndRef.current.scrollIntoView) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, phase, scrollToBottom]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);


  const handleSend = () => {
    if (!inputValue.trim() || isLoading) return;
    if (inputValue.trim().length > MAX_QUESTION_LENGTH) {
      setInputWarning(`Question too long (${inputValue.trim().length}/${MAX_QUESTION_LENGTH} characters). Please shorten your question.`);
      return;
    }
    setInputWarning(null);
    sendMessage(inputValue);
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleResetChat = () => {
    clearMessages();
    setAvailablePlans([]);
    setLockedPlanId(null);
    setLockedDate(null);
    if (hasPlanContext) {
      setLockedPlanId(reduxPlanId);
      setLockedDate(reduxDate);
      setPhase(ShiftPlanAgentPhase.CONFIRMING);
    } else {
      setPhase(ShiftPlanAgentPhase.AWAITING_INPUT);
    }
  };

  // Plan selection handlers
  const handleUseCurrentPlan = () => {
    setPhase(ShiftPlanAgentPhase.ACTIVE);
  };

  const handleChooseDifferent = () => {
    setPhase(ShiftPlanAgentPhase.AWAITING_INPUT);
  };

  const handleSubmitSearch = async (date: string, shiftType: ShiftSearchType) => {
    setPhase(ShiftPlanAgentPhase.FETCHING_PLANS);

    try {
      const response = await getAllShiftPlans(date, shiftType);
      if (phaseRef.current !== ShiftPlanAgentPhase.FETCHING_PLANS) return;

      const allPlans: SPMSGetShiftPlanResponse[] = response?.shiftPlans || [];
      setAvailablePlans(allPlans);
      setLockedDate(date);
      setPhase(ShiftPlanAgentPhase.SELECTING_PLAN);
    } catch {
      if (phaseRef.current !== ShiftPlanAgentPhase.FETCHING_PLANS) return;
      setAvailablePlans([]);
      setPhase(ShiftPlanAgentPhase.SELECTING_PLAN);
    }
  };

  const handleSelectPlan = (plan: SPMSGetShiftPlanResponse) => {
    setLockedPlanId(plan.planId as string);
    setLockedDate((plan.ofdDate as string) || lockedDate);
    setPhase(ShiftPlanAgentPhase.ACTIVE);
  };

  // Determine if the text input should be shown
  const showTextInput = phase === ShiftPlanAgentPhase.ACTIVE;

  // UI copy
  const INPUT_PLACEHOLDER = 'Ask about headcount decisions, staffing logic...';
  const EMPTY_STATE_HINT = 'Ask me about shift plan headcount decisions, staffing allocations, or why specific numbers were chosen.';

  return (
    <>
      {/* FAB Toggle Button */}
      {!isOpen && (
        <button
          className="agent-fab"
          onClick={() => setIsOpen(true)}
          aria-label="Open RCC Assistant"
        >
          <span className="agent-fab__icon">&#9889;</span>
        </button>
      )}

      {/* Chat Panel */}
      <div className={`agent-panel ${isOpen ? 'agent-panel--open' : ''}`}>
        {/* Header */}
        <div className="agent-panel__header">
          <div className="agent-panel__header-left">
            <div className="agent-panel__icon-wrap">&#9889;</div>
            <div className="agent-panel__title-text">
              <span className="agent-panel__title">RCC Assistant</span>
              <span className="agent-panel__subtitle">AI-powered operations insight</span>
            </div>
          </div>
          <div className="agent-panel__header-actions">
            <button
              className="agent-panel__reset-chat"
              onClick={handleResetChat}
              aria-label="Reset chat"
              title="Reset chat"
            >
              &#8635;
            </button>
            <button
              className="agent-panel__close"
              onClick={() => setIsOpen(false)}
              aria-label="Close RCC Assistant"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Plan Badge (active phase only) */}
        {phase === ShiftPlanAgentPhase.ACTIVE && (
          <div className="agent-panel__plan-badge">
            <span className="agent-panel__plan-badge-icon">&#128203;</span>
            <span className="agent-panel__plan-badge-text">
              {stationCode} &middot; {lockedDate} &middot; {lockedPlanId}
            </span>
          </div>
        )}

        {/* Messages */}
        <div className="agent-panel__messages">
          {/* Plan selection / confirmation (not fully ready) */}
          {phase !== ShiftPlanAgentPhase.ACTIVE && (
            <PlanSelectionMessage
              key={phase}
              phase={phase}
              currentNodeId={stationCode}
              currentDate={reduxDate}
              currentPlanId={reduxPlanId}
              availablePlans={availablePlans}
              onUseCurrentPlan={handleUseCurrentPlan}
              onChooseDifferent={handleChooseDifferent}
              onSubmitSearch={handleSubmitSearch}
              onSelectPlan={handleSelectPlan}
            />
          )}

          {/* Messages (shown once a plan is selected and chat is ready) */}
          {showTextInput && messages.length === 0 && (
            <div className="agent-panel__empty">
              <div className="agent-panel__empty-icon">&#9889;</div>
              <p>{EMPTY_STATE_HINT}</p>
            </div>
          )}
          {showTextInput && messages.map(msg => (
            <AgentMessage key={msg.id} message={msg} />
          ))}
          {isLoading && (
            <div className="agent-typing">
              <div className="agent-typing__dot" />
              <div className="agent-typing__dot" />
              <div className="agent-typing__dot" />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area — only shown when ready to chat */}
        {showTextInput && inputWarning && (
          <div className="agent-panel__input-warning">{inputWarning}</div>
        )}
        {showTextInput && (
          <div className="agent-panel__input-area">
            <input
              ref={inputRef}
              className="agent-panel__input"
              type="text"
              placeholder={INPUT_PLACEHOLDER}
              value={inputValue}
              onChange={e => { setInputValue(e.target.value); setInputWarning(null); }}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
            />
            <button
              className="agent-panel__send"
              onClick={handleSend}
              disabled={!inputValue.trim() || isLoading}
              aria-label="Send message"
            >
              &#10148;
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default AgentChatPanel;

import './AgentChatPanel.scss';